import { cva } from "class-variance-authority";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { NotificationSettingRow } from "./NotificationSettingRow";
import { getDomainIcon } from "../notification-icon";
import { getSettingGroupDescription } from "../setting-utils";
import type { GroupedNotificationSettings, NotificationSettingEntry, NotificationSettingText } from "../types";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/v2/card";
import { CaptainAvatar } from "@/features/captain/components/CaptainAvatar";
import { useAuth } from "@/lib/hooks/useAuth";

const domainIconVariants = cva("flex size-10 shrink-0 items-center justify-center rounded-full", {
  variants: {
    domain: {
      campaigns: "bg-(--badge-info-bg) text-(--badge-info-text)",
      integrations: "bg-(--badge-success-bg) text-(--badge-success-text)",
    },
  },
});

type NotificationSettingGroupProps = {
  group: GroupedNotificationSettings;
  getText: (id: NotificationSettingEntry["id"]) => NotificationSettingText;
};

function GroupMark({ domain }: { domain: GroupedNotificationSettings["id"] }) {
  if (domain === "captain") {
    return (
      <span aria-hidden className="shrink-0">
        <CaptainAvatar className="size-10" />
      </span>
    );
  }

  const Icon = getDomainIcon(domain);
  return (
    <div className={domainIconVariants({ domain })}>
      <Icon className="size-5" aria-hidden />
    </div>
  );
}

export function NotificationSettingGroup({ group, getText }: NotificationSettingGroupProps) {
  const { t } = useTranslation();
  const { context } = useAuth();
  const titleId = useId();

  return (
    <Card role="region" aria-labelledby={titleId} data-testid={`notification-setting-group-${group.id}`}>
      <CardHeader className="flex items-center gap-4">
        <GroupMark domain={group.id} />
        <div className="flex min-w-0 flex-col gap-1.5">
          <CardTitle id={titleId} role="heading" aria-level={2}>
            {t(`notificationSettings.groups.${group.id}.title`)}
          </CardTitle>
          <CardDescription>{getSettingGroupDescription(t, group.id, context)}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-0">
        <ul className="divide-y divide-border border-t border-border">
          {group.entries.map((entry) => (
            <NotificationSettingRow key={entry.id} entry={entry} text={getText(entry.id)} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
